import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { AppLoading } from "../AppLoading/AppLoading";

// AppLoading's logo draw: last path starts at 1.1s and animates for 1s.
const LOGO_ANIMATION_MS = 2100;

// ─── Keyframes ──────────────────────────────────────────────────────────────

const logoBloom = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.85);
    filter: blur(4px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0px);
  }
`;

const textPulse = keyframes`
  0%, 100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
`;

/* Loader exits: fades out while gently scaling up — like a curtain lifting */
const overlayExit = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(1.03);
  }
`;

// ─── Styled Components ───────────────────────────────────────────────────────

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: ${(props) => props.theme.palette.background.default};
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999999;
  pointer-events: ${(props) => (props.$fadeOut ? "none" : "all")};

  /* When fadeOut triggers, play the exit animation once */
  animation: ${(props) =>
    props.$fadeOut
      ? css`${overlayExit} 0.75s cubic-bezier(0.25, 1, 0.5, 1) forwards`
      : "none"};
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 1rem;
`;

const TatvaLogo = styled.img`
  width: 16rem;
  max-width: 80vw;
  height: auto;
  align-self: center;
  margin-bottom: 1.75rem;
  animation: ${logoBloom} 0.85s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    width: 12rem;
    margin-bottom: 1.25rem;
  }
`;

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.palette.redesign.pink};
  font-size: 1.125rem;
  text-align: center;
  font-weight: 700;
  margin-left: 20px;
  text-transform: uppercase;
  letter-spacing: 2px;
  align-self: center;
  padding: 0 20px;
  animation: ${textPulse} 1.8s ease-in-out infinite;

  ${({ theme }) => theme.breakpoints.up("sm")} {
    font-size: 1.5rem;
  }
`;

const Wrapper = styled.div`
  /*
   * While the loader is visible the content stays invisible.
   * Once $revealed flips to true we transition it in smoothly,
   * creating a cross-fade with the departing overlay.
   */
  opacity: ${({ $revealed }) => ($revealed ? 1 : 0)};
  transition: ${({ $revealed, $duration, $easing, $delay }) =>
    $revealed
      ? `opacity ${$duration}ms ${$easing} ${$delay}ms`
      : "none"};
`;

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * MountAnimation — wraps any component/children and animates them on mount.
 * If isLoading is provided, it handles the global brand logo loading overlay.
 */
export function MountAnimation({
  children,
  isLoading = false,
  variant = "fadeUp",
  duration = 600,
  delay = 0,
  easing = "cubic-bezier(0.22, 1, 0.36, 1)",
  className,
}) {
  const [showOverlay, setShowOverlay] = useState(isLoading);
  const [fadeOut, setFadeOut] = useState(false);
  // Controls the children's smooth fade-in — flips slightly before the overlay finishes
  const [revealed, setRevealed] = useState(!isLoading);
  const overlayShownAt = useRef(null);

  useEffect(() => {
    if (showOverlay && overlayShownAt.current === null) {
      overlayShownAt.current = Date.now();
    }
  }, [showOverlay]);

  useEffect(() => {
    let revealTimeout;
    let fadeTimeout;
    let hideTimeout;

    // Nothing to dismiss if the overlay was never shown (e.g. isLoading started false)
    if (!isLoading && showOverlay) {
      // Keep the overlay only until the AppLoading logo animation has finished;
      // if loading took longer than that, dismiss it straight away.
      const elapsed = Date.now() - (overlayShownAt.current ?? Date.now());
      const remaining = Math.max(0, LOGO_ANIMATION_MS - elapsed);

      fadeTimeout = setTimeout(() => {
        setFadeOut(true);

        // Start revealing children ~100ms into the overlay exit — smooth cross-fade
        revealTimeout = setTimeout(() => {
          setRevealed(true);
        }, 100);

        // Remove the overlay from the DOM after the exit animation finishes
        hideTimeout = setTimeout(() => {
          setShowOverlay(false);
        }, 800);
      }, remaining);
    }

    return () => {
      clearTimeout(revealTimeout);
      clearTimeout(fadeTimeout);
      clearTimeout(hideTimeout);
    };
  }, [isLoading]);

  return (
    <>
      {showOverlay && (
        <LoaderWrapper $fadeOut={fadeOut}>
          <AppLoading />
        </LoaderWrapper>
      )}

      {/*
       * Children always render so assets preload in the background.
       * The Wrapper is invisible (opacity 0) while the overlay is present,
       * then smoothly transitions to opacity 1 as the overlay fades away.
       */}
      <Wrapper
        $revealed={revealed}
        $variant={variant}
        $duration={duration}
        $delay={delay}
        $easing={easing}
        className={className}
        style={showOverlay && !revealed ? { overflow: "hidden", maxHeight: "100vh" } : {}}
      >
        {children}
      </Wrapper>
    </>
  );
}
